<script setup lang="ts">
    import type { AgentReasoning } from 'models'
    import { computed } from 'vue'
    import {
        ANTHROPIC_THINKING_EFFORTS,
        effectiveProviderReasoning,
        GENERIC_REASONING_EFFORTS,
        GOOGLE_THINKING_LEVELS,
        isGenericReasoningProvider,
        OPENAI_REASONING_EFFORTS,
        OPENAI_REASONING_SUMMARIES,
    } from 'models'
    import { useI18n } from 'vue-i18n'
    import PkCheckboxField from '../PkCheckboxField.vue'

    const { t: $t } = useI18n({ useScope: 'global' })

    const props = defineProps<{
        activeProvider: string
    }>()

    const reasoning = defineModel<AgentReasoning>('reasoning', {
        default: undefined,
    })

    /**
     * What the turn will actually send, per provider. An agent that configures
     * nothing gets the platform default (`low` everywhere it exists), so the
     * fields show that instead of an empty value: the previous empty state read
     * as "no reasoning" while the model was in fact thinking.
     *
     * The level fields are also not clearable (`min-values="1"`). Clearing one
     * used to write `{ thinkingLevel: undefined }`, which becomes `{}` in the
     * database and is indistinguishable from an absent configuration: the agent
     * then silently followed the provider's own default. Saying "do not think"
     * has its own explicit value on every provider (`none`, `disabled`,
     * `minimal`), and that is the way to say it.
     */
    const openai = computed(() =>
        effectiveProviderReasoning('openai', reasoning.value),
    )
    const anthropic = computed(() =>
        effectiveProviderReasoning('anthropic', reasoning.value),
    )
    const google = computed(() =>
        effectiveProviderReasoning('google', reasoning.value),
    )
    /**
     * The single-level providers share one field, so the block is looked up by
     * the active provider instead of having a computed each: they differ only in
     * which key of the configuration they read and write.
     */
    const generic = computed(() =>
        isGenericReasoningProvider(props.activeProvider)
            ? effectiveProviderReasoning(props.activeProvider, reasoning.value)
            : undefined,
    )

    /**
     * True while a field is showing the platform default rather than a stored
     * choice: the helper returns the agent's own block by identity, so the
     * comparison is enough. Drives the hint, because a value the user never
     * picked must not look like one they did.
     */
    const isDefault = computed(() => ({
        openai: openai.value !== reasoning.value?.openai,
        anthropic: anthropic.value !== reasoning.value?.anthropic,
        google: google.value !== reasoning.value?.google,
        generic:
            isGenericReasoningProvider(props.activeProvider) &&
            generic.value !== reasoning.value?.[props.activeProvider],
    }))

    /**
     * Every option list here is built from the enum the request is validated
     * against, levels and summary styles alike, so a value a provider gains
     * cannot be reachable in one list and missing from the other: hand-written
     * twice, they had already drifted on `max`. The names stay untranslated on
     * purpose, they are the provider's own API values.
     */
    const LEVEL_LABELS: Record<string, string> = {
        auto: 'Auto',
        concise: 'Concise',
        detailed: 'Detailed',
        none: 'None',
        minimal: 'Minimal',
        low: 'Low',
        medium: 'Medium',
        high: 'High',
        xhigh: 'XHigh',
        max: 'Max',
    }

    const levelOptions = (levels: readonly string[]) =>
        levels.map((value) => ({ label: LEVEL_LABELS[value] ?? value, value }))

    const openaiEffortOptions = levelOptions(OPENAI_REASONING_EFFORTS)
    const anthropicEffortOptions = levelOptions(ANTHROPIC_THINKING_EFFORTS)
    const googleLevelOptions = levelOptions(GOOGLE_THINKING_LEVELS)
    const genericEffortOptions = levelOptions(GENERIC_REASONING_EFFORTS)
    const summaryOptions = levelOptions(OPENAI_REASONING_SUMMARIES)
</script>

<template>
    <!-- OpenAI -->
    <template v-if="activeProvider === 'openai'">
        <VvCombobox
            :model-value="openai?.reasoningEffort"
            name="reasoning.openai.reasoningEffort"
            icon="ri:brain-line"
            strategy="fixed"
            :min-values="1"
            :label="$t('label.reasoningEffort')"
            :placeholder="$t('placeholder.select')"
            :hint-label="
                isDefault.openai ? $t('hint.reasoningDefault') : undefined
            "
            :options="openaiEffortOptions"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    openai: {
                        ...reasoning?.openai,
                        reasoningEffort: $event,
                    },
                }
            " />
        <VvCombobox
            v-if="openai?.reasoningEffort && openai.reasoningEffort !== 'none'"
            :model-value="openai?.reasoningSummary"
            name="reasoning.openai.reasoningSummary"
            strategy="fixed"
            icon="ri:file-text-line"
            :label="$t('label.reasoningSummary')"
            :placeholder="$t('placeholder.select')"
            class="mb-0"
            :options="summaryOptions"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    openai: {
                        ...reasoning?.openai,
                        reasoningSummary: $event,
                    },
                }
            " />
    </template>
    <!-- Anthropic -->
    <template v-else-if="activeProvider === 'anthropic'">
        <VvCombobox
            :model-value="anthropic?.type"
            name="reasoning.anthropic.type"
            icon="ri:brain-line"
            strategy="fixed"
            :min-values="1"
            :label="$t('label.thinkingType')"
            :placeholder="$t('placeholder.select')"
            :hint-label="
                isDefault.anthropic ? $t('hint.reasoningDefault') : undefined
            "
            :options="[
                { label: 'Adaptive', value: 'adaptive' },
                { label: 'Enabled (legacy)', value: 'enabled' },
                { label: 'Disabled', value: 'disabled' },
            ]"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    anthropic: {
                        ...reasoning?.anthropic,
                        type: $event,
                    },
                }
            " />
        <VvCombobox
            v-if="anthropic?.type === 'adaptive'"
            :model-value="anthropic?.effort"
            name="reasoning.anthropic.effort"
            strategy="fixed"
            icon="ri:speed-line"
            :min-values="1"
            :label="$t('label.thinkingEffort')"
            :placeholder="$t('placeholder.select')"
            :options="anthropicEffortOptions"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    anthropic: {
                        ...reasoning?.anthropic,
                        effort: $event,
                    },
                }
            " />
        <VvCombobox
            v-if="anthropic?.type === 'adaptive'"
            :model-value="anthropic?.display"
            name="reasoning.anthropic.display"
            strategy="fixed"
            icon="ri:eye-line"
            :label="$t('label.thinkingDisplay')"
            :placeholder="$t('placeholder.select')"
            :options="[
                { label: 'Summarized', value: 'summarized' },
                { label: 'Omitted', value: 'omitted' },
            ]"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    anthropic: {
                        ...reasoning?.anthropic,
                        display: $event,
                    },
                }
            " />
        <VvInputText
            v-if="anthropic?.type === 'enabled'"
            :model-value="anthropic?.budgetTokens"
            name="reasoning.anthropic.budgetTokens"
            type="number"
            min="1"
            step="1000"
            :label="$t('label.budgetTokens')"
            :placeholder="$t('placeholder.insert')"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    anthropic: {
                        ...reasoning?.anthropic,
                        budgetTokens: $event ? Number($event) : undefined,
                    },
                }
            " />
    </template>
    <!-- Google -->
    <template v-else-if="activeProvider === 'google'">
        <VvCombobox
            :model-value="google?.thinkingLevel"
            name="reasoning.google.thinkingLevel"
            icon="ri:brain-line"
            strategy="fixed"
            :min-values="1"
            :label="$t('label.thinkingLevel')"
            :placeholder="$t('placeholder.select')"
            :hint-label="
                isDefault.google ? $t('hint.reasoningDefault') : undefined
            "
            :options="googleLevelOptions"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    google: {
                        ...reasoning?.google,
                        thinkingLevel: $event,
                    },
                }
            " />
        <VvInputText
            :model-value="google?.thinkingBudget"
            name="reasoning.google.thinkingBudget"
            type="number"
            min="0"
            step="1024"
            :label="$t('label.thinkingBudget')"
            :placeholder="$t('placeholder.insert')"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    google: {
                        ...reasoning?.google,
                        thinkingBudget: $event ? Number($event) : undefined,
                    },
                }
            " />
        <PkCheckboxField
            :model-value="google?.includeThoughts ?? false"
            name="reasoning.google.includeThoughts"
            class="mb-0"
            switch
            :label="$t('label.includeThoughts')"
            :hint-label="$t('hint.includeThoughts')"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    google: {
                        ...reasoning?.google,
                        includeThoughts: $event,
                    },
                }
            " />
    </template>
    <!-- DeepSeek: no platform default here, and the field stays clearable.
         Thinking is intrinsic to the model (there are `-thinking` ids), so
         "leave it to the provider" is a legitimate state on this provider. -->
    <template v-else-if="activeProvider === 'deepseek'">
        <VvCombobox
            :model-value="reasoning?.deepseek?.type"
            name="reasoning.deepseek.type"
            icon="ri:brain-line"
            strategy="fixed"
            :label="$t('label.thinking')"
            :placeholder="$t('placeholder.select')"
            :options="[
                { label: $t('label.enabled'), value: 'enabled' },
                { label: $t('label.disabled'), value: 'disabled' },
            ]"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    deepseek: { type: $event },
                }
            " />
    </template>
    <!-- Providers configured by a single effort level. The level travels as the
         standardized option, so the field is the same on all of them and only
         the configuration key changes. -->
    <template v-else-if="isGenericReasoningProvider(activeProvider)">
        <VvCombobox
            :model-value="generic?.reasoningEffort"
            :name="`reasoning.${activeProvider}.reasoningEffort`"
            icon="ri:brain-line"
            strategy="fixed"
            class="mb-0"
            :min-values="1"
            :label="$t('label.reasoningEffort')"
            :placeholder="$t('placeholder.select')"
            :hint-label="
                isDefault.generic ? $t('hint.reasoningDefault') : undefined
            "
            :options="genericEffortOptions"
            @update:model-value="
                reasoning = {
                    ...reasoning,
                    [activeProvider]: { reasoningEffort: $event },
                }
            " />
    </template>
    <!-- Other providers -->
    <template v-else>
        <small class="text-12 text-word-3">
            {{ $t('hint.reasoningNotSupported') }}
        </small>
    </template>
</template>
